import type { IEnterprise } from "@bbj-types/enterprise";
import type { IOffer } from "@bbj-types/offer";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button, CircularProgress, type IconButtonProps, Stack, Typography } from "@mui/material";
import { AutocompleteEnterprises, type IEnterpriseAutocomplete, IconButtonVariant } from "@styleguide";
import { type MouseEventHandler, useState } from "react";
import { OfferCardEnterprisePopover, popoverClasses } from "./styles";

interface IApplyPopover {
  ButtonProps?: Omit<IconButtonProps, "onClick">;
  enterprise: IEnterprise | undefined;
  onEdit: (enterprise: IEnterpriseAutocomplete | null) => Promise<void>;
  matchedEnterprise?: IOffer["matchedEnterprise"];
  loading?: boolean;
}

export const ApplyPopover = ({ ButtonProps, enterprise, onEdit, matchedEnterprise, loading }: IApplyPopover) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [newEnterprise, setNewEnterprise] = useState<IEnterpriseAutocomplete | null>(null);

  const handlePopoverOpen: MouseEventHandler<HTMLButtonElement> = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    setAnchorEl(evt.currentTarget);
  };

  const handlePopoverClose: MouseEventHandler = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <IconButtonVariant
        variant="elevated"
        size="small"
        {...ButtonProps}
        className={ButtonProps?.className}
        onClick={handlePopoverOpen}
      >
        <EditOutlinedIcon color="primary" />
      </IconButtonVariant>
      <OfferCardEnterprisePopover
        className={popoverClasses.root}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        elevation={0}
      >
        {matchedEnterprise && (
          <>
            <Stack sx={{ paddingBottom: 1 }}>
              <Typography variant="body1" className={popoverClasses.textDescription}>
                Cette candidature a été reliée automatiquement à une entreprise correspondante dans la base de données
                d&apos;Horizon. Si elle ne correspond pas, vous pouvez la changer :
              </Typography>
            </Stack>
            <AutocompleteEnterprises
              TextFieldProps={{ label: "Entreprise" }}
              value={newEnterprise}
              onChange={(_, value) => {
                setNewEnterprise(value);
              }}
              disableAddEnterprise
            />
            <Typography variant="body1" className={popoverClasses.textOriginalName}>
              <Typography variant="body1SemiBold">Nom initial sur l&apos;offre importée : </Typography>
              <span className={popoverClasses.uppercase}>{enterprise?.name || "-"}</span>
            </Typography>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Button
                type="button"
                variant="text"
                sx={{ textDecoration: "underline", paddingLeft: 0 }}
                onClick={(event) => {
                  onEdit(null);
                  handlePopoverClose(event);
                }}
              >
                Supprimer le lien avec l&apos;entreprise
              </Button>
              <IconButtonVariant
                variant="elevated"
                onClick={(event) => {
                  if (newEnterprise || newEnterprise === null) {
                    onEdit(newEnterprise);
                  }
                  handlePopoverClose(event);
                }}
              >
                {loading ? <CircularProgress size="small" /> : <CheckIcon color="success" />}
              </IconButtonVariant>
            </Stack>
          </>
        )}
        {!matchedEnterprise && (
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Typography variant="body1" className={popoverClasses.textDescription}>
              Vous pouvez relier manuellement cette candidature à une existante dans la base de données d&apos;Horizon :
            </Typography>
            <AutocompleteEnterprises
              TextFieldProps={{ label: "Entreprise" }}
              value={newEnterprise}
              onChange={(_, value) => {
                setNewEnterprise(value);
              }}
              disableAddEnterprise
            />
            <Stack
              direction="row"
              sx={{
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <IconButtonVariant variant="elevated" size="medium" onClick={handlePopoverClose} disabled={loading}>
                <CloseIcon color="primary" />
              </IconButtonVariant>
              <IconButtonVariant
                variant="elevated"
                type="submit"
                size="medium"
                onClick={(event) => {
                  onEdit(newEnterprise);
                  handlePopoverClose(event);
                }}
              >
                {loading ? <CircularProgress size="small" /> : <CheckIcon color="success" />}
              </IconButtonVariant>
            </Stack>
          </Stack>
        )}
      </OfferCardEnterprisePopover>
    </>
  );
};
